import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Check, Clock, Target, Trophy, Flame } from 'lucide-react';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useProtocolProgress } from '@/hooks/useProtocolProgress';
import AchievementUnlock from '@/components/gamification/AchievementUnlock';

interface DailyStep {
  day: number;
  title: string;
  description: string;
  checklist: string[];
}

export default function ProtocolDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState('');
  const [hasStarted, setHasStarted] = useState(false);
  const [unlockedAchievement, setUnlockedAchievement] = useState<any>(null);
  const analytics = useAnalytics();
  const { checkAchievements } = useProtocolProgress();

  const { data: protocol } = useQuery({
    queryKey: ['protocol', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('protocols')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      return data;
    }
  });

  const { data: progress } = useQuery({
    queryKey: ['protocol-progress', id],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('protocol_progress')
        .select('*')
        .eq('user_id', user.id)
        .eq('protocol_id', id);
      
      if (error) throw error;
      return data || [];
    }
  });

  const updateProgress = useMutation({
    mutationFn: async ({ day, completed, notes }: { day: number; completed: boolean; notes?: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('protocol_progress')
        .upsert({
          user_id: user.id,
          protocol_id: id!,
          day,
          completed,
          notes
        }, {
          onConflict: 'user_id,protocol_id,day'
        });

      if (error) throw error;
    },
    onSuccess: async (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['protocol-progress', id] });
      queryClient.invalidateQueries({ queryKey: ['user-active-protocols'] });
      queryClient.invalidateQueries({ queryKey: ['all-protocol-progress'] });
      toast.success('Progress saved');
      
      // Track protocol completion
      if (variables.completed && protocol) {
        analytics.trackFeatureUsage('protocol', 'day_completed', {
          protocol_id: protocol.id,
          protocol_name: protocol.name,
          day: variables.day
        });
        
        // Check for new achievements
        setTimeout(() => {
          checkAchievements();
        }, 500);
      }
    }
  });

  // Check for newly earned achievements
  const { data: latestAchievement } = useQuery({
    queryKey: ['latest-protocol-achievement'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from('user_achievements')
        .select(`
          achievement_id,
          unlocked_at,
          achievements:achievement_id (*)
        `)
        .eq('user_id', user.id)
        .order('unlocked_at', { ascending: false })
        .limit(1)
        .single();

      if (error) return null;
      
      // Only show if unlocked in the last 10 seconds
      const unlockTime = new Date(data.unlocked_at).getTime();
      const now = Date.now();
      if (now - unlockTime < 10000) {
        return data.achievements;
      }
      return null;
    },
    refetchInterval: 2000
  });

  useEffect(() => {
    if (latestAchievement && !unlockedAchievement) {
      setUnlockedAchievement(latestAchievement);
    }
  }, [latestAchievement, unlockedAchievement]);

  // Track when user starts protocol
  useEffect(() => {
    if (protocol && !hasStarted && progress && progress.length === 0) {
      setHasStarted(true);
      analytics.trackFeatureUsage('protocol', 'started', {
        protocol_id: protocol.id,
        protocol_name: protocol.name
      });
    }
  }, [protocol, progress, hasStarted, analytics]);

  if (!protocol) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Loading protocol...</h2>
        </div>
      </div>
    );
  }

  const dailySteps: DailyStep[] = (protocol.daily_steps as any) || [];
  const completedDays = progress?.filter(p => p.completed).length || 0;
  const progressPercentage = (completedDays / protocol.duration_days) * 100;

  const currentDay = completedDays + 1;
  const currentStep = dailySteps.find(s => s.day === currentDay);
  const isProtocolComplete = completedDays === protocol.duration_days;

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Button
          variant="ghost"
          onClick={() => navigate('/longevity')}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Longevity
        </Button>

        <div className="mb-8">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold mb-2">{protocol.name}</h1>
              <p className="text-muted-foreground">{protocol.description}</p>
            </div>
            <Badge variant={isProtocolComplete ? 'default' : 'outline'}>
              {isProtocolComplete ? 'Complete' : `Day ${currentDay}/${protocol.duration_days}`}
            </Badge>
          </div>

          <Card className="p-6 bg-muted/30">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                <span className="font-medium">Your Progress</span>
              </div>
              <span className="text-sm text-muted-foreground">
                {completedDays} of {protocol.duration_days} days
              </span>
            </div>
            <Progress value={progressPercentage} className="h-3 mb-3" />
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="w-4 h-4" />
              <span>Expected IL impact: {protocol.expected_il_delta_min} to {protocol.expected_il_delta_max} points</span>
            </div>
          </Card>
        </div>

        {!isProtocolComplete && currentStep && (
          <Card className="p-6 mb-6 border-primary/30 bg-primary/5">
            <div className="flex items-center gap-2 mb-4">
              <div className="p-2 rounded-lg bg-primary/10">
                <Check className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold">Today: Day {currentStep.day}</h3>
                <p className="text-sm text-muted-foreground">{currentStep.title}</p>
              </div>
            </div>

            <p className="text-sm mb-4">{currentStep.description}</p>

            <div className="space-y-3 mb-4">
              <p className="text-sm font-medium">Today's Checklist:</p>
              {currentStep.checklist.map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <Checkbox id={`check-${index}`} className="mt-0.5" />
                  <label
                    htmlFor={`check-${index}`}
                    className="text-sm leading-relaxed cursor-pointer"
                  >
                    {item}
                  </label>
                </div>
              ))}
            </div>

            <Textarea
              placeholder="Notes about today's practice..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mb-4"
              rows={3}
            />

            <Button
              onClick={() => updateProgress.mutate({ day: currentDay, completed: true, notes })}
              disabled={updateProgress.isPending}
              className="w-full"
            >
              {updateProgress.isPending ? 'Saving...' : 'Complete Today'}
            </Button>
          </Card>
        )}

        {isProtocolComplete && (
          <Card className="p-6 mb-6 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Protocol Complete! 🎉</h3>
              <p className="text-sm text-muted-foreground mb-4">
                You've completed all {protocol.duration_days} days. Check your IL score to see the impact.
              </p>
              <div className="flex gap-3 justify-center">
                <Button onClick={() => navigate('/longevity/inflammation')}>
                  View IL Score
                </Button>
                <Button variant="outline" onClick={() => navigate('/longevity')}>
                  Start Another Protocol
                </Button>
              </div>
            </div>
          </Card>
        )}

        <div className="space-y-4">
          <h3 className="font-semibold text-lg">All Days</h3>
          {dailySteps.map((step) => {
            const dayProgress = progress?.find(p => p.day === step.day);
            const isCompleted = dayProgress?.completed || false;
            const isCurrent = step.day === currentDay && !isProtocolComplete;

            return (
              <Card
                key={step.day}
                className={`p-4 ${isCurrent ? 'border-primary' : ''} ${isCompleted ? 'opacity-75' : ''}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isCompleted ? 'bg-primary text-primary-foreground' : 
                    isCurrent ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'
                  }`}>
                    {isCompleted ? <Check className="w-5 h-5" /> : step.day}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium mb-1">{step.title}</h4>
                    <p className="text-sm text-muted-foreground mb-2">{step.description}</p>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      {step.checklist.map((item, idx) => (
                        <li key={idx}>• {item}</li>
                      ))}
                    </ul>
                    {dayProgress?.notes && (
                      <p className="text-xs text-muted-foreground mt-2 italic border-l-2 border-muted pl-2">
                        {dayProgress.notes}
                      </p>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Achievement Unlock Modal */}
      <AchievementUnlock 
        achievement={unlockedAchievement}
        onClose={() => setUnlockedAchievement(null)}
      />
    </div>
  );
}
